import { useChatActions } from '@ai-sdk-tools/store';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { ArrowUpIcon, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { useAttachments } from './attachments-provider';
import { useChatInputContext } from './chat-input-context';
import { hasUnsyncedAttachments } from '../../../../lib/chat-workspace';
import type { UIMessageWithMetadata } from '../../../../lib/types';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { novaGradientClass } from '../../design-system';
import { normalizeChatUuid } from '../../lib/notification-helpers';

interface SendButtonProps {
  className?: string;
  disabled?: boolean;
}

const DURABLE_STOP_FEATURE_FLAG = 'nova-durable-stop';

function DurableStopButton({ className }: Pick<SendButtonProps, 'className'>) {
  const { stop } = useChatActions<UIMessageWithMetadata>();
  const [isStopping, setIsStopping] = useState(false);

  return (
    <Button
      aria-label="Stop"
      aria-busy={isStopping}
      data-testid="stop-button"
      className={cn(
        'h-fit rounded-full border-none bg-clip-padding p-2',
        novaGradientClass.aiBackground,
        'text-brand-500 hover:opacity-90',
        className,
      )}
      disabled={isStopping}
      onClick={async (event) => {
        event.preventDefault();
        setIsStopping(true);
        try {
          await stop();
        } catch {
          setIsStopping(false);
        }
      }}
      type="button"
    >
      <span className="block size-3 rounded-[2px] bg-white" />
    </Button>
  );
}

/**
 * Send button component for chat input.
 * Uses context to access input state and submit handler.
 * Shows loading state when chat is submitting/streaming.
 */
export function SendButton({ className, disabled: externalDisabled }: SendButtonProps) {
  const { input, handleSubmit, status } = useChatInputContext();
  const isDurableStopEnabled = useFeatureFlag(DURABLE_STOP_FEATURE_FLAG, false);
  const { attachments } = useAttachments();
  const { additionalContext, chatBackend, chatUuid } = useChatOverlayContext();

  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const hasUnsyncedAttachmentContext = hasUnsyncedAttachments(attachments, additionalContext);
  const isDisabled =
    externalDisabled ||
    (input.length === 0 && attachments.length === 0) ||
    hasUploadingFiles ||
    hasUnsyncedAttachmentContext;

  if (['submitted', 'streaming'].includes(status)) {
    if (isDurableStopEnabled && chatBackend.kind === 'nova-v2') {
      return <DurableStopButton key={normalizeChatUuid(chatUuid)} className={className} />;
    }

    return (
      <Button
        data-testid="loading-button"
        className={cn(
          'h-fit rounded-full border-none bg-clip-padding p-2',
          novaGradientClass.aiBackground,
          'text-brand-500 hover:opacity-90',
          className,
        )}
        disabled
        type="button"
      >
        <Loader2 size={14} className="animate-spin" />
      </Button>
    );
  }

  return (
    <Button
      data-testid="send-button"
      className={cn(
        'h-fit rounded-full border-none bg-clip-padding p-2 transition-none',
        !isDisabled && novaGradientClass.aiBackground,
        !isDisabled && 'hover:opacity-90',
        'border-transparent',
        isDisabled ? 'text-[var(--color-steel-200)]' : 'text-[var(--color-wtw-ultraviolet-850)]',
        className,
        {
          'cursor-not-allowed disabled:border-transparent disabled:bg-[var(--color-steel-100)] disabled:opacity-100':
            isDisabled,
        },
      )}
      onClick={(event) => {
        event.preventDefault();
        if (!isDisabled) {
          handleSubmit(undefined);
        }
      }}
      disabled={isDisabled}
    >
      <ArrowUpIcon size={14} />
    </Button>
  );
}
